<template>
  <form @submit="onSubmit">
    <h2>{{ t('basicInfo') }}</h2>
    <div class="mt-4 grid grid-cols-3 gap-4">
      <div class="grid grid-cols-1 gap-2">
        <SelectField name="makeId" :label="t('make')" :options="makeOptions" />

        <InputField name="model" :label="t('model')" />

        <MultiSelectField
          name="categories"
          value-prop="value"
          mode="tags"
          :label="t('categories')"
          :options="categories"
        />

        <SelectField name="mainCategory" :label="t('mainCategory')" :options="chosenCategories" />
      </div>

      <div class="col-span-2 grid grid-cols-6 gap-4">
        <img
          v-if="imageUrl"
          class="col-span-4 mb-3 self-center"
          :src="imageUrl"
          :width="600"
          :height="300"
        />
        <div
          v-else
          class="col-span-4 flex h-52 w-full items-center justify-center self-center rounded-md bg-white text-primary"
        >
          {{ t('noImage') }}
        </div>
        <div class="col-span-2 space-y-2 self-center">
          <CVButton class="w-full" @click.prevent="showUploadImageModal = true">
            {{ t('changeImage') }}
          </CVButton>
          <CVButton
            v-if="formValues.imageFile"
            variant="danger"
            class="w-full"
            @click.prevent="cancelImageChange"
          >
            {{ t('resetImage') }}
          </CVButton>
        </div>
      </div>
    </div>

    <Divider />

    <h2>{{ t('dataForPriceCalculation') }}</h2>

    <div class="mt-4 grid grid-cols-3 gap-4">
      <InputField
        v-if="$can('update', 'VehicleType', 'grossPurchasePrice')"
        :min="0"
        name="grossPurchasePrice"
        :label="t('purchasePrice')"
        adornment="€"
        :max-fraction-digits="2"
        type="number"
        :tooltip="t('purchasePriceInfo')"
      />

      <InputField
        v-if="$can('update', 'VehicleType', 'basePrice')"
        :min="0"
        name="basePrice"
        :label="t('basePrice')"
        adornment="€"
        :max-fraction-digits="2"
        type="number"
        :hint="t('basePriceHint')"
      />

      <OverrideDefaultValueField
        v-slot="{ defaultInputBinds }"
        name="customDeposit"
        type="number"
        :default-value="calculatedDeposit?.deposit ?? null"
        :formatted-default-value="
          calculatedDeposit ? formatCurrency(calculatedDeposit.deposit) : '-'
        "
        :is-loading-default="isFetchingDeposit"
        :label="t('deposit')"
      >
        <Input
          type="number"
          :max-fraction-digits="2"
          :min="0"
          adornment="€"
          v-bind="defaultInputBinds"
        />
      </OverrideDefaultValueField>
    </div>

    <Discount :initial-vehicle-type="initialVehicleType" class="mt-4" />

    <Divider />

    <h2>{{ t('technicalData') }}</h2>

    <div class="mb-4 mt-4 grid grid-cols-3 gap-4">
      <SelectField name="fuelType" :label="t('fuelType')" :options="fuelTypes" />

      <SelectField name="transmission" :label="t('transmission')" :options="transmissionTypes" />

      <SelectField name="drive" :label="t('drive')" :options="driveTypes" />

      <InputField
        v-if="formValues.fuelType === FuelType.ELECTRIC"
        name="batteryCapacityInKwh"
        :label="t('batteryCapacity')"
        adornment="kWh"
        type="number"
        :min="0"
        empty-value-to="null"
      />

      <InputField
        v-else
        :min="0"
        name="tankVolumeInLiter"
        :label="t('tankVolume')"
        adornment="l"
        type="number"
        empty-value-to="null"
      />

      <InputField
        v-if="formValues.fuelType === FuelType.ELECTRIC"
        :min="0"
        name="rangeInKm"
        :label="t('range')"
        adornment="km"
        type="number"
      />

      <SeatsInputFields :label="t('seats')" :seats-min="1" />

      <InputField :min="0" name="power" :label="t('power')" adornment="PS" type="number" />

      <InputField
        :min="0"
        name="accelaration"
        :label="t('accelaration')"
        adornment="s"
        :max-fraction-digits="2"
        type="number"
      />
    </div>

    <CheckboxField
      name="isAlwaysOnWinterTyres"
      :initial-value="initialValues.isAlwaysOnWinterTyres"
      :label="t('alwaysOnWinterTyres')"
    />

    <Divider />

    <h2>{{ t('features') }}</h2>

    <div class="mt-4 grid grid-cols-3 gap-4">
      <CheckboxField
        name="hasNavigation"
        :initial-value="initialValues.hasNavigation"
        :label="t('gpsNavigation')"
      />

      <CheckboxField
        name="hasDistanceControl"
        :initial-value="initialValues.hasDistanceControl"
        :label="t('distanceControl')"
      />

      <CheckboxField
        name="hasRearViewCamera"
        :initial-value="initialValues.hasRearViewCamera"
        :label="t('rearViewCamera')"
      />
    </div>

    <Divider />

    <h2>{{ t('more') }}</h2>

    <div class="mt-4 grid grid-cols-3 gap-4">
      <InputField
        :min="0"
        name="inspectionIntervalInKm"
        :label="t('inspectionInterval')"
        adornment="km"
        type="number"
      />

      <OverrideDefaultValueField
        v-slot="{ defaultInputBinds }"
        name="customMinAge"
        type="number"
        :default-value="calculatedMinAge?.minAge ?? null"
        :formatted-default-value="calculatedMinAge?.minAge ?? '-'"
        :is-loading-default="isFetchingMinAge"
        :label="t('minAge')"
      >
        <Input type="number" :max-fraction-digits="0" :min="0" v-bind="defaultInputBinds" />
      </OverrideDefaultValueField>
      <OverrideDefaultValueField
        v-slot="{ defaultInputBinds }"
        name="customPricePerExtraKm"
        type="number"
        :default-value="calculatedPricePerExtraKm?.pricePerExtraKm ?? null"
        :formatted-default-value="
          calculatedPricePerExtraKm
            ? formatCurrency(calculatedPricePerExtraKm.pricePerExtraKm)
            : '-'
        "
        :is-loading-default="isFetchingPricePerExtraKm"
        :label="t('pricePerExtraKm')"
      >
        <Input
          type="number"
          :max-fraction-digits="2"
          :min="0"
          adornment="€"
          v-bind="defaultInputBinds"
        />
      </OverrideDefaultValueField>

      <InputField name="acrissCode" :label="t('acrissCode')" type="text" empty-value-to="null" />
    </div>

    <Divider />

    <h2>{{ t('vehicleTypesForInsurances') }}</h2>

    <div class="mt-4 grid grid-cols-3 gap-4">
      <SelectField name="axaCategory" :label="t('axa')" :options="axaTypes" empty-value-to="null" />
      <SelectField
        name="rolandCategory"
        :label="t('roland')"
        :options="rolandTypes"
        empty-value-to="null"
      />
      <SelectField
        name="europCategory"
        :label="t('europ.label')"
        :options="europTypes"
        empty-value-to="null"
      />
      <SelectField
        name="volvoCategory"
        :label="t('volvo')"
        :options="volvoTypes"
        empty-value-to="null"
      />
      <SelectField name="jlrCategory" :label="t('jlr')" :options="jlrTypes" empty-value-to="null" />
    </div>

    <Divider />

    <CustomContractCheckboxes name="customContractCheckboxes" />

    <Divider />

    <ExtraDeductible />

    <Divider />

    <h2>{{ t('promotionalVideo') }}</h2>

    <PromotionalVideoField
      name="promotionalVideoMediaId"
      :initial-media="initialVehicleType?.promotionalVideo"
      class="mt-4"
    />

    <Divider />

    <div class="flex justify-end">
      <CVButton :is-loading="isSaving" type="submit" :disabled="isSaving">
        {{ t('save') }}
      </CVButton>
    </div>

    <SelectImageForUploadModal
      v-model:show-modal="showUploadImageModal"
      :image-file="formValues.imageFile"
      @select="onSelectImage"
    ></SelectImageForUploadModal>
  </form>
</template>

<script lang="ts" setup>
import CheckboxField from '@/components/CheckboxField.vue';
import Divider from '@/components/Divider.vue';
import InputField from '@/components/InputField.vue';
import PromotionalVideoField from '@/components/media/PromotionalVideoField.vue';
import SeatsInputFields from '@/components/SeatsInputFields.vue';
import MultiSelectField from '@/components/MultiSelectField.vue';
import { DriveType } from '@/entities/cars/drive-type.enum';
import { FuelType } from '@/entities/cars/fuel-type.enum';
import { TransmissionType } from '@/entities/cars/transmission-type.enum';
import { VehicleCategory } from '@/entities/vehicle-category.enum';
import type {
  VehicleExtraFormValues,
  VehicleExtraOptionFormValues,
} from '@/entities/vehicle-extra.entity';
import type { UpdateVehicleTypeDto, VehicleType } from '@/entities/vehicle-type.entity';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { useI18n } from 'vue-i18n';
import { useMakes } from '@/queries/use-makes';
import { vehicleTypeValidator } from '@/validation/vehicle-type.schema';
import { useField, useForm } from 'vee-validate';
import { computed, reactive, ref, toRef, watch } from 'vue';
import SelectField from '@/components/SelectField.vue';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import SelectImageForUploadModal from '@/components/SelectImageForUploadModal.vue';
import Discount from './Discount.vue';
import CustomContractCheckboxes from '@/components/vehicle-forms/CustomContractCheckboxes.vue';
import ExtraDeductible from '@/components/vehicle-forms/ExtraDeductible.vue';
import {
  AxaVehicleCategory,
  EuropVehicleCategory,
  JlrVehicleCategory,
  VehicleExtraType,
  VolvoVehicleCategory,
} from '@carvia/ros-client-types';
import { RolandVehicleCategory } from '@/entities/vehicle-type.entity';
import { formatCurrency } from '@/utils/format-numbers.ts';
import OverrideDefaultValueField from '@/components/OverrideDefaultValueField.vue';
import {
  useCalculatePricePerExtraKm,
  useGetDeposit,
  useGetMinAge,
} from '@/queries/use-yield-management-settings.ts';
import { refDebounced } from '@vueuse/core';
import Input from '@/components/Input.vue';

export type VehicleTypeFormValues = UpdateVehicleTypeDto & {
  extraDeductible: VehicleExtraFormValues;
  overrideDeductibleExtra: boolean;
  imageFile: File | undefined;
};

const props = defineProps<{
  initialVehicleType?: VehicleType;
  isSaving: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: VehicleTypeFormValues): void;
}>();

const { t } = useI18n();

const showUploadImageModal = ref(false);
const imageUrl = ref(props.initialVehicleType?.imageUrl);

const initialValues = reactive<VehicleTypeFormValues>({
  model: props.initialVehicleType?.model,
  categories: props.initialVehicleType?.categories,
  mainCategory: props.initialVehicleType?.mainCategory,
  grossPurchasePrice: props.initialVehicleType?.grossPurchasePrice,
  basePrice: props.initialVehicleType?.basePrice,
  fuelType: props.initialVehicleType?.fuelType,
  transmission: props.initialVehicleType?.transmission,
  drive: props.initialVehicleType?.drive,
  batteryCapacityInKwh: props.initialVehicleType?.batteryCapacityInKwh,
  tankVolumeInLiter: props.initialVehicleType?.tankVolumeInLiter,
  rangeInKm: props.initialVehicleType?.rangeInKm,
  seats: props.initialVehicleType?.seats,
  additionalSeats: props.initialVehicleType?.additionalSeats ?? 0,
  power: props.initialVehicleType?.power,
  accelaration: props.initialVehicleType?.accelaration,
  inspectionIntervalInKm: props.initialVehicleType?.inspectionIntervalInKm,
  acrissCode: props.initialVehicleType?.acrissCode,
  axaCategory: props.initialVehicleType?.axaCategory,
  rolandCategory: props.initialVehicleType?.rolandCategory,
  europCategory: props.initialVehicleType?.europCategory,
  volvoCategory: props.initialVehicleType?.volvoCategory,
  jlrCategory: props.initialVehicleType?.jlrCategory,
  customContractCheckboxes: props.initialVehicleType?.customContractCheckboxes,
  extraDeductible: getInitialExtra(VehicleExtraType.DEDUCTIBLE),
  overrideDeductibleExtra: !!getInitialExtra(VehicleExtraType.DEDUCTIBLE).id,
  availableExtraIds: props.initialVehicleType?.availableExtras.map((extra) => extra.id) ?? [],
  makeId: props.initialVehicleType?.make.id,
  imageFile: undefined,
  hasDistanceControl: props.initialVehicleType?.hasDistanceControl ?? false,
  hasNavigation: props.initialVehicleType?.hasNavigation ?? false,
  hasRearViewCamera: props.initialVehicleType?.hasRearViewCamera ?? false,
  discountFrom: props.initialVehicleType?.discountFrom ?? null,
  discountUntil: props.initialVehicleType?.discountUntil ?? null,
  discountFactor: props.initialVehicleType?.discountFactor ?? null,
  isAlwaysOnWinterTyres: props.initialVehicleType?.isAlwaysOnWinterTyres ?? false,
  customDeposit: props.initialVehicleType?.customDeposit ?? null,
  customMinAge: props.initialVehicleType?.customMinAge ?? null,
  customPricePerExtraKm: props.initialVehicleType?.customPricePerExtraKm ?? null,
  promotionalVideoMediaId: props.initialVehicleType?.promotionalVideo?.id ?? null,
});

function getInitialExtra(type: VehicleExtraType) {
  const extraName = 'Deductible';
  const extra = props.initialVehicleType?.availableExtras.find(
    (extra) => extra.type === type && !extra.autoAssign,
  );
  const initialExtra = extra && {
    id: extra.id,
    type: extra.type,
    options: extra.options.map((option) => ({
      ...option,
      value: Number(option.value),
    })) as VehicleExtraOptionFormValues[],
  };
  return (
    initialExtra ?? {
      id: undefined,
      description: `${extraName} - ${props.initialVehicleType?.make.name} ${props.initialVehicleType?.model}`,
      type: type,
      options: [],
    }
  );
}

const fuelTypes = computed(() =>
  Object.values(FuelType).map((value) => ({
    label: t(`fuelTypes.${value}`),
    value,
  })),
);

const transmissionTypes = computed(() =>
  Object.values(TransmissionType).map((value) => ({
    label: t(`transmissionTypes.${value}`),
    value,
  })),
);

const driveTypes = computed(() =>
  Object.values(DriveType).map((value) => ({
    label: t(`driveTypes.${value}`),
    value,
  })),
);

const categories = computed(() =>
  Object.values(VehicleCategory).map((value) => ({
    label: t(`categories.${value}`),
    value,
  })),
);

const axaTypes = computed(() => [
  { label: t('noCategory'), value: null },
  ...Object.values(AxaVehicleCategory).map((value) => ({
    label: value,
    value,
  })),
]);

const rolandTypes = computed(() => [
  { label: t('noCategory'), value: null },
  ...Object.values(RolandVehicleCategory).map((value) => ({
    label: t(`rolandCategories.${value}`),
    value,
  })),
]);

const europTypes = computed(() => [
  { label: t('noCategory'), value: null },
  ...Object.values(EuropVehicleCategory).map((value) => ({
    label: t(`europCategories.${value}`),
    value,
  })),
]);

const volvoTypes = computed(() => [
  { label: t('noCategory'), value: null },
  ...Object.values(VolvoVehicleCategory).map((value) => ({
    label: t(`volvoCategories.${value}`),
    value,
  })),
]);

const jlrTypes = computed(() => [
  { label: t('noCategory'), value: null },
  ...Object.values(JlrVehicleCategory).map((value) => ({
    label: t(`jlrCategories.${value}`),
    value,
  })),
]);

const chosenCategories = computed(
  () =>
    formValues.categories?.map((value) => ({
      label: t(`categories.${value}`),
      value,
      tooltip: t(`categories.${value}`),
    })) ?? [],
);

const { data: makes } = useMakes();

const makeOptions = computed(() => makes.value?.map((m) => ({ label: m.name, value: m.id })) ?? []);

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm<VehicleTypeFormValues>({
  initialValues,
  validationSchema: vehicleTypeValidator,
});

const unchangedValues = ref({ ...formValues });

const { value: grossPurchasePrice } = useField<number>('grossPurchasePrice');
watch(grossPurchasePrice, (newPrice) => {
  setFieldValue('basePrice', Math.round((newPrice / 1.19) * 0.0025 * 100) / 100);
});

const netPurchasePrice = computed(() => {
  if (formValues.grossPurchasePrice) {
    return formValues.grossPurchasePrice / 1.19;
  }
  return null;
});

watch(
  () => formValues.acrissCode,
  (newAcrissCode) => {
    setFieldValue('acrissCode', newAcrissCode?.toUpperCase());
  },
);

const debouncedNetPurchasePrice = refDebounced(netPurchasePrice, 300);
const debouncedCategory = refDebounced(toRef(formValues, 'mainCategory'), 300);

const pricePerExtraKmQuery = computed(() => ({
  netPurchasePrice: debouncedNetPurchasePrice.value!,
  category: debouncedCategory.value!,
}));
const pricePerExtraKmEnabled = computed(
  () =>
    !!(
      debouncedNetPurchasePrice.value &&
      debouncedCategory.value &&
      typeof formValues.customPricePerExtraKm !== 'number'
    ),
);
const { data: calculatedPricePerExtraKm, isFetching: isFetchingPricePerExtraKm } =
  useCalculatePricePerExtraKm(pricePerExtraKmQuery, pricePerExtraKmEnabled);

const depositAndMinAgeQuery = computed(() => ({
  netPurchasePrice: debouncedNetPurchasePrice.value!,
}));
const depositEnabled = computed(
  () => !!(debouncedNetPurchasePrice.value && typeof formValues.customDeposit !== 'number'),
);
const { data: calculatedDeposit, isFetching: isFetchingDeposit } = useGetDeposit(
  depositAndMinAgeQuery,
  depositEnabled,
);

const minAgeEnabled = computed(
  () => !!(debouncedNetPurchasePrice.value && typeof formValues.customMinAge !== 'number'),
);
const { data: calculatedMinAge, isFetching: isFetchingMinAge } = useGetMinAge(
  depositAndMinAgeQuery,
  minAgeEnabled,
);

const onSelectImage = (newImageFile: File, newImageUrl: string) => {
  setFieldValue('imageFile', newImageFile);
  imageUrl.value = newImageUrl;
};

const cancelImageChange = () => {
  setFieldValue('imageFile', undefined);
  imageUrl.value = props.initialVehicleType?.imageUrl;
};

const onSubmit = handleSubmit((values) => {
  unchangedValues.value = { ...formValues };
  emit('onSubmit', values);
});

usePreventLeavingUnsavedForm(() => formHasChanged(unchangedValues.value, formValues));
</script>

<i18n lang="json">
{
  "en": {
    "basicInfo": "Basic Info",
    "make": "Make",
    "model": "Model",
    "categories": "Categories",
    "mainCategory": "Main Category",
    "changeImage": "Change Image",
    "dataForPriceCalculation": "Data for Price Calculation",
    "purchasePrice": "Purchase Price",
    "purchasePriceInfo": "The purchase price is the gross purchase price incl. discount",
    "basePrice": "Base Price",
    "deposit": "Deposit",
    "technicalData": "Technical Data",
    "fuelType": "Fuel Type",
    "drive": "Drive",
    "tankVolume": "Tank Volume",
    "batteryCapacity": "Battery Capacity",
    "range": "Range",
    "transmission": "Transmission",
    "seats": "Seats",
    "additionalSeats": "Additional seats",
    "power": "Power",
    "accelaration": "Acceleration (0 - 100)",
    "features": "Features",
    "gpsNavigation": "GPS/Navigation",
    "distanceControl": "Distance Control ...",
    "rearViewCamera": "... incl. Rear View Camera",
    "more": "More",
    "minAge": "Minimal Age",
    "pricePerExtraKm": "Price per Extra KM",
    "backToOverview": "Back to Vehicle Types Overview",
    "noImage": "No Image",
    "basePriceHint": "Default: 0.25% of the net resell price",
    "resetImage": "Reset image",
    "inspectionInterval": "Inspection Interval",
    "alwaysOnWinterTyres": "Always on winter tyres (should never be changed to summer tyres)",
    "vehicleTypesForInsurances": "Vehicle types for partner insurances",
    "axa": "AXA",
    "roland": "Roland",
    "volvo": "Volvo Assistance",
    "jlr": "JLR Assistance",
    "noCategory": "- No category -",
    "acrissCode": "ACRISS Code",
    "promotionalVideo": "Promotional video",
    "europ": {
      "label": "Europ Assistance"
    }
  },
  "de": {
    "basicInfo": "Grundlegende Daten",
    "make": "Marke",
    "model": "Modell",
    "categories": "Kategorien",
    "mainCategory": "Hauptkategorie",
    "changeImage": "Bild ändern",
    "dataForPriceCalculation": "Daten zur Preisberechnung",
    "purchasePrice": "Einkaufspreis",
    "purchasePriceInfo": "Der Einkaufspreis ist der Brutto EK inkl. Nachlass",
    "basePrice": "Basispreis",
    "deposit": "Kaution",
    "technicalData": "Technische Daten",
    "fuelType": "Treibstofftyp",
    "drive": "Antrieb",
    "tankVolume": "Tankgröße",
    "batteryCapacity": "Batteriekapazität",
    "range": "Reichweite",
    "transmission": "Getriebe",
    "seats": "Sitze",
    "additionalSeats": "Zusätzliche Sitze",
    "power": "Leistung",
    "accelaration": "Beschleunigung (0 - 100)",
    "features": "Ausstattung",
    "gpsNavigation": "GPS/Navigation",
    "distanceControl": "Parkabstandswarner ...",
    "rearViewCamera": "... inkl. Rückfahrkamera",
    "more": "Weiteres",
    "minAge": "Mindestalter",
    "pricePerExtraKm": "Zusatzkilometerpreis",
    "backToOverview": "Zurück zur Vehicle Types Übersicht",
    "noImage": "Kein Bild",
    "basePriceHint": "Standard: 0.25% vom Netto Einkaufspreis",
    "resetImage": "Bild zurücksetzen",
    "inspectionInterval": "Inspektionsintervall",
    "alwaysOnWinterTyres": "Immer auf Winterreifen (sollen nie auf Sommerreifen gewechselt werden)",
    "vehicleTypesForInsurances": "Fahrzeugtypen für Partnerversicherungen",
    "axa": "AXA",
    "roland": "Roland",
    "volvo": "Volvo Assistance",
    "jlr": "JLR Assistance",
    "noCategory": "- Keine Kategorie -",
    "acrissCode": "ACRISS Code",
    "promotionalVideo": "Werbevideo",
    "europ": {
      "label": "Europ Assistance"
    }
  },
  "es": {
    "basicInfo": "Datos básicos",
    "make": "Marca",
    "model": "Modelo",
    "categories": "Categorías",
    "mainCategory": "Categoría principal",
    "changeImage": "Cambiar imagen",
    "dataForPriceCalculation": "Datos para el cálculo de precio",
    "purchasePrice": "Precio de compra",
    "purchasePriceInfo": "El precio de compra es el bruto de compra incl. descuento",
    "basePrice": "Precio base",
    "deposit": "Depósito",
    "technicalData": "Datos técnicos",
    "fuelType": "Combustible",
    "drive": "Tracción",
    "tankVolume": "Capacidad del depósito",
    "batteryCapacity": "Capacidad de batería",
    "range": "Autonomía",
    "transmission": "Transmisión",
    "seats": "Plazas",
    "additionalSeats": "Plazas adicionales",
    "power": "Potencia",
    "accelaration": "Aceleración (0–100)",
    "features": "Equipamiento",
    "gpsNavigation": "GPS/Navegación",
    "distanceControl": "Control de distancia…",
    "rearViewCamera": "… incl. cámara trasera",
    "more": "Más",
    "minAge": "Edad mínima",
    "pricePerExtraKm": "Precio por km adicional",
    "backToOverview": "Volver a la vista general de tipos de vehículo",
    "noImage": "Sin imagen",
    "basePriceHint": "Por defecto: 0,25 % del precio neto de reventa",
    "resetImage": "Restablecer imagen",
    "inspectionInterval": "Intervalo de inspección",
    "alwaysOnWinterTyres": "Siempre con neumáticos de invierno (no deben cambiarse a verano)",
    "vehicleTypesForInsurances": "Tipos de vehículo para seguros partner",
    "axa": "AXA",
    "roland": "Roland",
    "volvo": "Volvo Assistance",
    "jlr": "JLR Assistance",
    "noCategory": "- Sin categoría -",
    "acrissCode": "Código ACRISS",
    "promotionalVideo": "Vídeo promocional",
    "europ": {
      "label": "Europ Assistance"
    }
  }
}
</i18n>
